import * as Headless from '@headlessui/react'
import { clsx } from 'clsx'
import React from 'react'

// Estilos base compartidos por todos los botones
const baseStyles = clsx(
  // Layout y tipografía
  'relative inline-flex items-center justify-center gap-2',
  'rounded-lg border font-semibold',
  'text-base/6 sm:text-sm/6',
  // Padding
  'px-3.5 py-2.5 sm:px-3 sm:py-1.5',
  // Cursor
  'cursor-pointer',
  // Focus
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 dark:focus-visible:ring-primary-400 focus-visible:ring-offset-2',
  // Disabled
  'disabled:opacity-50 disabled:cursor-not-allowed',
  // Transiciones
  'transition-colors duration-150'
)

// Variantes de estilos
const variantStyles = {
  'primary-solid': clsx(
    'bg-gray-950 text-white border-transparent',
    'hover:bg-gray-800',
    'active:bg-gray-700',
    'dark:bg-gray-100 dark:text-gray-900',
    'dark:hover:bg-gray-200',
    'dark:active:bg-gray-300'
  ),
  'secondary-solid': clsx(
    'bg-gray-100 text-gray-900 border-transparent',
    'hover:bg-gray-200',
    'active:bg-gray-300',
    'dark:bg-gray-800 dark:text-gray-100',
    'dark:hover:bg-gray-700',
    'dark:active:bg-gray-600'
  ),
  outline: clsx(
    'bg-transparent border-gray-300 text-gray-700',
    'hover:bg-gray-100 hover:border-gray-400',
    'active:bg-gray-200',
    'dark:border-gray-600 dark:text-gray-300',
    'dark:hover:bg-gray-700 dark:hover:border-gray-500',
    'dark:active:bg-gray-600'
  ),
  plain: clsx(
    'bg-transparent border-transparent text-gray-500',
    'hover:text-gray-700',
    'active:text-gray-800',
    'dark:text-gray-400',
    'dark:hover:text-gray-200',
    'dark:active:text-gray-100'
  ),
}

// Colores adicionales
const colorStyles = {
  primary: clsx(
    'bg-primary-600 text-white border-transparent',
    'hover:bg-primary-500',
    'active:bg-primary-700',
    'dark:bg-primary-500',
    'dark:hover:bg-primary-400',
    'dark:active:bg-primary-600'
  ),
  sky: clsx(
    'bg-sky-600 text-white border-transparent',
    'hover:bg-sky-500',
    'active:bg-sky-700',
    'dark:bg-sky-500',
    'dark:hover:bg-sky-400',
    'dark:active:bg-sky-600'
  ),
  zinc: clsx(
    'bg-gray-600 text-white border-transparent',
    'hover:bg-gray-500',
    'active:bg-gray-700',
    'dark:bg-gray-400 dark:text-gray-900',
    'dark:hover:bg-gray-300',
    'dark:active:bg-gray-500'
  ),
  red: clsx(
    'bg-red-600 text-white border-transparent',
    'hover:bg-red-500',
    'active:bg-red-700',
    'dark:bg-red-500',
    'dark:hover:bg-red-400',
    'dark:active:bg-red-600'
  ),
  green: clsx(
    'bg-green-600 text-white border-transparent',
    'hover:bg-green-500',
    'active:bg-green-700',
    'dark:bg-green-500',
    'dark:hover:bg-green-400',
    'dark:active:bg-green-600'
  ),
  emerald: clsx(
    'bg-emerald-600 text-white border-transparent',
    'hover:bg-emerald-500',
    'active:bg-emerald-700',
    'dark:bg-emerald-500',
    'dark:hover:bg-emerald-400',
    'dark:active:bg-emerald-600'
  ),
  amber: clsx(
    'bg-amber-400 text-amber-950 border-transparent',
    'hover:bg-amber-300',
    'active:bg-amber-500',
    'dark:bg-amber-500 dark:text-amber-950',
    'dark:hover:bg-amber-400',
    'dark:active:bg-amber-600'
  ),
}

type ButtonProps = (
  | { color?: keyof typeof colorStyles; outline?: never; plain?: never; variant?: never }
  | { color?: never; outline: true; plain?: never; variant?: never }
  | { color?: never; outline?: never; plain: true; variant?: never }
  | { color?: never; outline?: never; plain?: never; variant: keyof typeof variantStyles }
) & {
  className?: string
  children: React.ReactNode
} & Omit<React.ComponentPropsWithoutRef<'button'>, 'className'>

export function Button({
  color,
  outline,
  plain,
  variant,
  className,
  children,
  ...props
}: ButtonProps) {
  const styles = clsx(
    baseStyles,
    variant
      ? variantStyles[variant]
      : outline
        ? variantStyles.outline
        : plain
          ? variantStyles.plain
          : color
            ? colorStyles[color]
            : variantStyles['primary-solid'], // default
    className
  )

  return (
    <button type="button" {...props} className={styles}>
      {children}
    </button>
  )
}

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
